import { type ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { faPlus } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { faMagnifyingGlass } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import { Link } from '@inertiajs/react';

import { AddBlogModal } from '@/components/page-editor/add-blog-modal';
import { AddCaseResultModal } from '@/components/page-editor/add-case-result-modal';
import { AddPageModal } from '@/components/page-editor/add-page-modal';
import { LoadMoreSentinel } from '@/components/shared/load-more-sentinel';
import { REMOTE_SEARCH_DEBOUNCE_MS } from '@/components/shared/use-debounced-remote-search';
import { Button } from '@/components/ui/button';
import { TextInput } from '@/components/ui/form/text-input';
import { Icon } from '@/components/ui/icon';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from '@/components/ui/sidebar';
import type { ItemChangedReason, TreeItems } from '@/components/ui/sortable-tree';
import { SortableTree } from '@/components/ui/sortable-tree';
import { Heading5, MutedText } from '@/components/ui/typography';
import { useDebounce } from '@/hooks/use-debounce';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { useVirtualRows } from '@/hooks/use-virtual-rows';
import { isScheduleMissed, isSchedulePendingOrInGrace } from '@/lib/page-scheduling';
import { cn } from '@/lib/utils';
import type { ActivePageTypeView } from '@/stores/nested-page-view-store';
import { usePagesStore } from '@/stores/pages-store';
import { type Page, PageType } from '@/types/page';
import { PageDropdownMenu } from './page-dropdown-menu';
import { PageContent } from './page-item';
import type { ReorderOperation } from './page-utils';
import { filterPages, INDEX_TO_CHILD_TYPE } from './page-utils';
import type { SortablePageItemData } from './sortable-page-item';
import { SortablePageItem } from './sortable-page-item';
import { useIsPageActive } from './use-is-page-active';
import { resolveReorderMode, usePageTypeReorder } from './use-page-type-reorder';

interface PageTypeViewProps {
  view: ActivePageTypeView;
  onLoadMore?: () => void | Promise<void>;
  onSearch?: (searchQuery: string) => void;
  onReorder?: (operation: ReorderOperation) => void;
}

interface PageTypeViewItemProps extends ComponentProps<'li'> {
  page: Page;
  canPin?: boolean;
}

function PageTypeViewItem({ page, canPin, ...itemProps }: PageTypeViewItemProps) {
  const isActive = useIsPageActive(page.id);
  const isPublished = page.isPublished;
  const hasUnpublishedChanges = page.hasUnpublishedChanges;
  const schedulePending = isSchedulePendingOrInGrace(page.scheduledPublishAt);
  const scheduleMissed = isScheduleMissed(page.scheduledPublishAt);
  const displayTitle = page.navigationTitle || page.title;

  const pageContent = (
    <PageContent
      pageType={page.pageType}
      title={displayTitle}
      isPublished={isPublished}
      hasUnpublishedChanges={hasUnpublishedChanges}
      isScheduled={schedulePending}
      isScheduleMissed={scheduleMissed}
      pinned={page.pinned}
    />
  );

  return (
    <SidebarMenuItem {...itemProps}>
      <SidebarMenuButton
        asChild={!!page.url}
        isActive={isActive}
        className={cn(
          'pr-7',
          !isPublished && 'opacity-50 text-sidebar-foreground/70',
          page.pinned && 'border-l-2 border-l-primary rounded-l-none bg-sidebar-accent/50',
        )}
      >
        {page.url ? <Link href={page.url}>{pageContent}</Link> : pageContent}
      </SidebarMenuButton>
      <PageDropdownMenu page={page} canPin={canPin} />
    </SidebarMenuItem>
  );
}

interface ReorderablePageListProps {
  items: TreeItems<SortablePageItemData>;
  onItemsChanged: (
    items: TreeItems<SortablePageItemData>,
    reason: ItemChangedReason<SortablePageItemData>,
  ) => void;
}

function ReorderablePageList({ items, onItemsChanged }: ReorderablePageListProps) {
  return (
    <SortableTree
      items={items}
      onItemsChanged={onItemsChanged}
      TreeItemComponent={SortablePageItem}
      dropAnimation={null}
      sortableProps={{ animateLayoutChanges: () => false }}
      indentationWidth={0}
    />
  );
}

interface StaticPageListProps {
  pages: Page[];
  canPin: boolean;
}

function StaticPageList({ pages, canPin }: StaticPageListProps) {
  const { listRef, virtualizer } = useVirtualRows({
    count: pages.length,
    getItemKey: (index) => pages[index]?.id ?? index,
    gap: 2,
  });

  return (
    <SidebarMenu ref={listRef} className="relative" style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((row) => {
        const page = pages[row.index];
        if (!page) return null;

        return (
          <PageTypeViewItem
            key={page.id}
            page={page}
            canPin={canPin}
            data-index={row.index}
            ref={virtualizer.measureElement}
            className="absolute top-0 left-0 w-full"
            style={{ transform: `translateY(${row.start}px)` }}
          />
        );
      })}
    </SidebarMenu>
  );
}

interface PinnedPageListProps {
  pinnedItems: TreeItems<SortablePageItemData>;
  onPinnedItemsChanged: (
    items: TreeItems<SortablePageItemData>,
    reason: ItemChangedReason<SortablePageItemData>,
  ) => void;
  unpinnedPages: Page[];
  canPin: boolean;
}

function PinnedPageList({
  pinnedItems,
  onPinnedItemsChanged,
  unpinnedPages,
  canPin,
}: PinnedPageListProps) {
  return (
    <>
      {pinnedItems.length > 0 && (
        <ReorderablePageList items={pinnedItems} onItemsChanged={onPinnedItemsChanged} />
      )}
      {unpinnedPages.length > 0 && <StaticPageList pages={unpinnedPages} canPin={canPin} />}
    </>
  );
}

function InitialPageListSkeleton() {
  const skeletons = Array.from({ length: 5 }, (_, index) => `nested-page-skeleton-${index}`);

  return (
    <div className="space-y-1">
      {skeletons.map((skeleton) => (
        <SidebarMenuSkeleton key={skeleton} showIcon className="px-2 py-2" />
      ))}
    </div>
  );
}

export function PageTypeView({ view, onLoadMore, onSearch, onReorder }: PageTypeViewProps) {
  const maxPinnedPages = usePagesStore((state) => state.maxPinnedPages);
  const {
    pageTitle,
    pageType,
    indexPage,
    children: pages,
    pagination,
    isIndexPage,
    searchQuery: currentSearchQuery = '',
    isLoadingInitial,
  } = view;
  const childPageType = INDEX_TO_CHILD_TYPE[pageType] || pageType;
  const reorderMode = resolveReorderMode(childPageType as PageType);
  const [searchQuery, setSearchQuery] = useState(currentSearchQuery);

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const visiblePages = useMemo(
    () => filterPages(pages, normalizedSearchQuery),
    [pages, normalizedSearchQuery],
  );

  const hasMorePages = Boolean(pagination && pagination.currentPage < pagination.totalPages);
  const hasSearchQuery = normalizedSearchQuery.length > 0;
  const shouldAutoLoad = Boolean(isIndexPage && hasMorePages && onLoadMore && !hasSearchQuery);
  const isResettingSearch = !hasSearchQuery && (currentSearchQuery ?? '').trim().length > 0;

  const handleLoadMore = useCallback(() => {
    if (!onLoadMore) return;
    return onLoadMore();
  }, [onLoadMore]);

  const { sentinelRef, isLoadingMore } = useInfiniteScroll({
    hasNextPage: shouldAutoLoad,
    isLoading: Boolean(isLoadingInitial),
    onLoadMore: handleLoadMore,
    rootMargin: '100px',
    threshold: 0.1,
  });

  const { localTreeItems, handleItemsChanged, unpinnedPages, pinLimitReached } = usePageTypeReorder(
    {
      pages,
      reorderMode,
      maxPinnedPages,
      onReorder,
    },
  );
  const debouncedSearch = useDebounce((nextQuery: string) => {
    onSearch?.(nextQuery);
  }, REMOTE_SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (!isIndexPage || !onSearch) {
      return;
    }

    debouncedSearch(searchQuery);
  }, [debouncedSearch, isIndexPage, onSearch, searchQuery]);

  const renderPageList = () => {
    if (isLoadingInitial || (isResettingSearch && pages.length === 0)) {
      return <InitialPageListSkeleton />;
    }

    if (pages.length === 0) {
      return (
        <div className="rounded-lg border border-dashed border-sidebar-border p-8 text-center">
          <MutedText className="text-xs">
            {normalizedSearchQuery
              ? `No matching ${pageTitle.toLowerCase()} found.`
              : `No ${childPageType === PageType.BLOG ? 'Blog Posts' : pageTitle} exist yet.`}
          </MutedText>
        </div>
      );
    }

    if (visiblePages.length === 0) {
      return (
        <div className="rounded-lg border border-dashed border-sidebar-border p-8 text-center">
          <MutedText className="text-xs">No matching {pageTitle.toLowerCase()} found.</MutedText>
        </div>
      );
    }

    if (hasSearchQuery) {
      return <StaticPageList pages={visiblePages} canPin={!pinLimitReached} />;
    }

    if (reorderMode === 'all') {
      return <ReorderablePageList items={localTreeItems} onItemsChanged={handleItemsChanged} />;
    }

    if (reorderMode === 'pinned-only') {
      return (
        <PinnedPageList
          pinnedItems={localTreeItems}
          onPinnedItemsChanged={handleItemsChanged}
          unpinnedPages={unpinnedPages}
          canPin={!pinLimitReached}
        />
      );
    }

    return <StaticPageList pages={pages} canPin={!pinLimitReached} />;
  };

  return (
    <SidebarGroup className="gap-4 px-4 py-4">
      <SidebarHeader className="p-0 text-foreground flex flex-row items-center justify-between gap-3">
        <Heading5 className="min-w-0 flex-1 truncate text-xl leading-7 font-bold text-foreground/90">
          {pageTitle}
        </Heading5>
        {childPageType === PageType.CASE_RESULT ? (
          <AddCaseResultModal
            trigger={
              <Button size="sm">
                <Icon icon={faPlus} />
                <span>Add</span>
              </Button>
            }
          />
        ) : childPageType === PageType.BLOG ? (
          <AddBlogModal
            trigger={
              <Button size="sm">
                <Icon icon={faPlus} />
                <span>Add</span>
              </Button>
            }
          />
        ) : (
          <AddPageModal
            childPageType={childPageType}
            trigger={
              <Button size="sm">
                <Icon icon={faPlus} />
                <span>Add</span>
              </Button>
            }
          />
        )}
      </SidebarHeader>
      <SidebarGroupContent className="space-y-4">
        <TextInput
          placeholder={`Search ${pageTitle.toLowerCase()}...`}
          leftSection={<Icon icon={faMagnifyingGlass} className="text-foreground" />}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          autoComplete="off"
        />
        {indexPage && (
          <SidebarMenu className="mb-0">
            <PageTypeViewItem page={indexPage} />
          </SidebarMenu>
        )}
        <div className="border-l border-sidebar-border/80 ml-5 pl-2">{renderPageList()}</div>
        <LoadMoreSentinel
          sentinelRef={sentinelRef}
          isLoadingMore={isLoadingMore}
          hasNextPage={shouldAutoLoad}
          className="px-4 py-2"
        />

        {pagination && !hasMorePages && visiblePages.length > 0 && (
          <div className="px-4 py-2">
            <MutedText className="text-xs text-center">
              {hasSearchQuery || isResettingSearch
                ? `Showing ${visiblePages.length} ${visiblePages.length === 1 ? 'result' : 'results'}`
                : `Showing all ${pagination.totalCount} ${pagination.totalCount === 1 ? 'item' : 'items'}`}
            </MutedText>
          </div>
        )}
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
